import { useCallback, useMemo, useState } from 'react';
import request from '../../../../../request';
import streamingRequest, {
  convertStreamingJsonToChunks,
} from '../../../../../streamingRequest';
import {
  CONVERSATIONAL_AI_STATUSES,
  CONVERSATIONAL_AI_VALUE_STATUSES,
  TRANSFORMATION_DATA_TYPE_JSON,
  CONVERSATIONAL_AI_MESSAGE_TYPES,
} from '../../../../../constants';

function useConversationalAiPrompt(sessionId) {
  const [dialog, setDialog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(
    CONVERSATIONAL_AI_VALUE_STATUSES.ONGOING_CONVERSATION,
  );
  const supportsStreaming = useMemo(() => {
    try {
      const fetchIsSupported = 'fetch' in window;
      const readableStreamIsSupported =
        'ReadableStream' in window && 'TextDecoder' in window;
      return fetchIsSupported && readableStreamIsSupported;
    } catch (error) {
      return false;
    }
  }, []);

  const updateLastMessage = useCallback(
    (additionalMessage) => {
      setDialog((prevDialog) => {
        const lastMessage = prevDialog[prevDialog.length - 1];
        if (lastMessage?.type === CONVERSATIONAL_AI_MESSAGE_TYPES.AI) {
          const updatedMessage =
            (lastMessage.message || '') + additionalMessage;
          return [
            ...prevDialog.slice(0, prevDialog.length - 1),
            { ...lastMessage, message: updatedMessage, visible: true },
          ];
        }
        return prevDialog;
      });
    },
    [setDialog],
  );
  const clearLastMessage = useCallback(() => {
    setDialog((prevDialog) => {
      const lastMessage = prevDialog[prevDialog.length - 1];
      if (lastMessage?.type === CONVERSATIONAL_AI_MESSAGE_TYPES.AI) {
        return [
          ...prevDialog.slice(0, prevDialog.length - 1),
          { ...lastMessage, message: '' },
        ];
      }
      return prevDialog;
    });
  }, [setDialog]);

  const fetchNextPrompt = useCallback(
    async (userInput = '', isInitialCall = false) => {
      if (!sessionId) throw new Error('Session ID is required');
      if (loading && !isInitialCall) {
        return;
      }
      if (status !== CONVERSATIONAL_AI_VALUE_STATUSES.ONGOING_CONVERSATION) {
        return;
      }

      const surveyId = Pollfish.survey.getSurveyId();
      try {
        setLoading(true);
        if (userInput) {
          setDialog((prevDialog) => [
            ...prevDialog,
            { type: CONVERSATIONAL_AI_MESSAGE_TYPES.USER, message: userInput },
            {
              type: CONVERSATIONAL_AI_MESSAGE_TYPES.AI,
              message: '',
              visible: false,
            },
          ]);
        } else {
          setDialog((prevDialog) => [
            ...prevDialog,
            {
              type: CONVERSATIONAL_AI_MESSAGE_TYPES.AI,
              message: '',
              visible: false,
            },
          ]);
        }

        const requestUrl = `/v2/device/conversational/chat/sessions/${sessionId}/prompt`;
        if (supportsStreaming) {
          await streamingRequest({
            url: requestUrl,
            method: 'POST',
            data: {
              prompt: userInput,
              survey_id: surveyId,
            },
            onChunks: (jsonChunks) => {
              handleResponseChunks({
                jsonChunks,
                sessionId,
                setStatus,
                updateMessage: updateLastMessage,
                clearLastMessage,
              });
            },
          });
        } else {
          const response = await request({
            url: requestUrl,
            method: 'POST',
            contentType: 'application/json; charset=utf-8',
            transformData: true,
            transformationDataType: TRANSFORMATION_DATA_TYPE_JSON,
            dataType: 'text',
            data: {
              prompt: userInput,
              survey_id: surveyId,
            },
          });
          const { jsonChunks } = convertStreamingJsonToChunks(response);

          handleResponseChunks({
            jsonChunks,
            sessionId,
            setStatus,
            updateMessage: updateLastMessage,
            clearLastMessage,
          });
        }
      } catch (err) {
        console.error(
          `Failed to communicate with AI service about the user prompt`,
          err,
        );
        Raven.captureMessage(
          'Failed to communicate with AI service about the user prompt',
          {
            extra: { sessionId, surveyId, supportsStreaming, err },
          },
        );
        setStatus(CONVERSATIONAL_AI_VALUE_STATUSES.FAILURE);
      } finally {
        setLoading(false);
      }
    },
    [status, loading, sessionId, supportsStreaming, updateLastMessage],
  );

  return { status, dialog, loading, fetchNextPrompt };
}

function handleResponseChunk({
  jsonChunk,
  sessionId,
  setStatus,
  updateMessage,
  clearLastMessage,
}) {
  const { type, value } = jsonChunk;

  if (type === CONVERSATIONAL_AI_STATUSES.ERROR) {
    // handle AI error
    throw new Error(`AI error (${value.error_status}): ${value.error_msg}`);
  }
  if (type === CONVERSATIONAL_AI_STATUSES.RETRY) {
    // handle AI retry
    clearLastMessage();
    return;
  }
  if (value.session_id !== sessionId) {
    Raven.captureMessage('Session ID mismatch in AI response', {
      extra: { value, type, jsonChunk },
    });
    throw new Error(
      `Session ID mismatch in AI response with sessionId: ${sessionId} and jsonChunkSessionId: ${value.session_id}`,
    );
  }
  switch (value?.type) {
    case CONVERSATIONAL_AI_VALUE_STATUSES.ONGOING_CONVERSATION:
      updateMessage(value.msg);
      break;
    case CONVERSATIONAL_AI_VALUE_STATUSES.SUCCESS:
    case CONVERSATIONAL_AI_VALUE_STATUSES.FAILURE:
      setStatus(value.type);
      break;
    default:
      throw new Error(`Unknown AI response type: ${value?.type}`);
  }
}

function handleResponseChunks({
  jsonChunks,
  sessionId,
  setStatus,
  updateMessage,
  clearLastMessage,
}) {
  jsonChunks.forEach((chunk) => {
    handleResponseChunk({
      jsonChunk: chunk,
      sessionId,
      setStatus,
      updateMessage,
      clearLastMessage,
    });
  });
}
export default useConversationalAiPrompt;
